import {gql} from '@apollo/client';
import React from 'react';

import {MaybeArchivableItem} from 'archive/ArchiveConstants';
import getArchiveAwareDisplayName from 'archive/getArchiveAwareDisplayName';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {BenchlingAppsDropdownQuery} from './__generated/BenchlingAppsDropdownQuery';

type SchemaFieldSelector = BenchlingAppsDropdownQuery['registries'][number]['schemaFieldSelectors'][number];

export interface SchemaFieldSelectorInputProps {
  value: string | null;
  onChange: (selectedDropdownId: string | null) => void;
  disabled: boolean;
}

export const BENCHLING_APPS_DROPDOWN_QUERY = gql`
  query BenchlingAppsDropdownQuery @skipAudit @engComponent(name: BENCHLING_APPS) {
    registries {
      id
      schemaFieldSelectors {
        name
        id
        ...HasArchiveRecordGraphQL
      }
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
`;

export default function SchemaFieldSelectorInput({
  value,
  onChange,
  disabled,
}: SchemaFieldSelectorInputProps): JSX.Element | null {
  const schemaFieldSelectorsResult = useBenchlingQuery<BenchlingAppsDropdownQuery>(
    BENCHLING_APPS_DROPDOWN_QUERY
  );
  let selectors: Array<SchemaFieldSelector>;
  if (schemaFieldSelectorsResult.type === QueryResultType.ERROR) {
    return null;
  } else if (schemaFieldSelectorsResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    selectors = schemaFieldSelectorsResult.data.registries
      .map((registry) => registry.schemaFieldSelectors)
      .flat();
  }

  const menuOptions =
    selectors.length === 0
      ? [{name: 'No dropdowns found'}]
      : [
          // Display no dropdown option
          {name: 'No dropdown', value: null},
          {
            name: 'Dropdowns',
            isHeader: true,
          },
          ...selectors.map((selector) => ({
            name: selector.name,
            value: selector.id,
          })),
        ];

  function getSelectorDisplayName(id: string | null, options: Array<MaybeArchivableItem>): string {
    if (id == null) {
      return 'No Dropdown Selected';
    }
    const selectedOption = options.find((option) => option.id === id);
    if (selectedOption) {
      return getArchiveAwareDisplayName(selectedOption);
    } else {
      return 'Inaccessible dropdown';
    }
  }
  return (
    <SelectDropdown
      displayName={getSelectorDisplayName(value, selectors)}
      disabled={disabled}
      menu={<SearchableMenu onSelect={onChange} optionDatas={menuOptions} selectedValue={value} />}
    />
  );
}
